import { queueCallProcessing } from 'app/lib/callProcessing'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useEffect, useState } from 'react'
import { graphql } from 'app/generated/gql'
import { PROMPTS } from 'lib/extractionPrompts'
import { gqlQuery } from 'app/lib/graphqlClient'
import { opsClient } from 'app/lib/opsUrqlClient'
import { logError } from 'lib/logging'

const EXTRACTION_DASHBOARD_QUERY = graphql(`
  query ExtractionDashboard {
    extractionDashboard {
      workspaceId
      shadowStats {
        total
        last24h
        avgMatchScore
        byWorkspace {
          name
          total
        }
      }
    }
  }
`)

export type WorkspaceStat = {
  workspaceId: string
  count: number
  avgScore: number
}

export type ShadowStats = {
  total: number
  last24h: number
  avgMatchScore: number
  byWorkspace: WorkspaceStat[]
}

export type PromptVersion = {
  stage: string
  version: string
}

export type UseOpsExtractionDashboardReturn = {
  workspaceId: string | null
  shadowStats: ShadowStats
  promptVersions: PromptVersion[]
  testCallId: string
  setTestCallId: (v: string) => void
  isRunning: boolean
  extractionData: any
  onRunExtraction: () => void
}

export function useOpsExtractionDashboard(): UseOpsExtractionDashboardReturn {
  const [workspaceId, setWorkspaceId] = useState<string | null>(null)
  const [shadowStats, setShadowStats] = useState<ShadowStats>({
    total: 0,
    last24h: 0,
    avgMatchScore: 0,
    byWorkspace: [],
  })
  const [testCallId, setTestCallId] = useState(``)
  const [isRunning, setIsRunning] = useState(false)
  const [extractionData, setExtractionData] = useState<any>()

  useEffect(() => {
    gqlQuery<{ extractionDashboard: { workspaceId: string; shadowStats: ShadowStats } }>(
      EXTRACTION_DASHBOARD_QUERY,
      undefined,
      { client: opsClient },
    )
      .then((result) => {
        const data = result.data?.extractionDashboard
        setWorkspaceId(data?.workspaceId ?? null)
        setShadowStats(data?.shadowStats ?? { total: 0, last24h: 0, avgMatchScore: 0, byWorkspace: [] })
      })
      .catch((err) => logError(`Error fetching extraction dashboard`, err))
  }, [])

  const promptVersions = Object.entries(PROMPTS).map(([stage, { version }]) => ({ stage, version }))

  const onRunExtraction = () => {
    if (testCallId.trim()) {
      setIsRunning(true)
      queueCallProcessing(testCallId.trim(), 'extract')
        .then(() => {
          setExtractionData({ queued: true, message: `Refresh the call after processing completes.` })
          notifySuccess(`Extraction queued`)
        })
        .catch((error) => {
          logError(`Could not queue extraction`, error)
          notifyError(error instanceof Error ? error.message : `Could not queue extraction`)
        })
        .finally(() => setIsRunning(false))
    }
  }

  return {
    workspaceId,
    shadowStats,
    promptVersions,
    testCallId,
    setTestCallId,
    isRunning,
    extractionData,
    onRunExtraction,
  }
}
